import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import _ from 'lodash';
import {useTranslation} from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import PreviewFilm from 'components/PreviewFilm';
import FilmCartouche from 'components/Film/FilmCartouche';
import Utils from 'utils/Utils';
import Context from 'utils/Context';

import './FilmsList.scss';

const SEE_MORE_LINES_INCREMENT = 4;

const FILM_NB_BY_LINE = {
  xl: 6,
  lg: 6,
  md: 4,
  sm: 3,
  xs: 2,
};

const FilmsList = ({films, onClick, canOpen}) => {
  const {t} = useTranslation();
  const [filmIdSelected, setFilmIdSelected] = useState(0);
  const [visibleLinesNb, setVisibleLinesNb] = useState(1);

  const [previewHeight, setPreviewHeight] = useState(100);
  const [previewTop, setPreviewTop] = useState(0);

  const {appState} = useContext(Context);
  const previewRef = useRef(null);
  const containerRef = useRef(null);

  const screenSize = appState.screenSize;

  const goToPreview = useCallback(() => {
    if (canOpen && filmIdSelected > 0) {
      const domRect = previewRef.current.getBoundingClientRect();
      setPreviewHeight(domRect.height);
    }
  }, [canOpen, filmIdSelected]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, previewTop);
    }, 1);
  }, [previewHeight, previewTop]);

  useEffect(() => {
    const handleScroll = e => {
      if (visibleLinesNb % SEE_MORE_LINES_INCREMENT === 0) {
        // every SEE_MORE_LINES_INCREMENT lines, no scroll, need to click the button
        return;
      }
      const areSomeFilmsHidden = visibleLinesNb < Math.ceil(films.length / FILM_NB_BY_LINE[screenSize]);
      if (areSomeFilmsHidden && containerRef.current.getBoundingClientRect().bottom <= window.innerHeight) {
        setVisibleLinesNb(old => old + 1);
      }
    };

    const resize = () => {
      if (screenSize !== 'xs') goToPreview();
    };

    window.addEventListener('resize', resize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [films.length, goToPreview, screenSize, visibleLinesNb]);

  const handleClickSeeMore = () => {
    setVisibleLinesNb(old => old + 1);
  };

  const handleSelectFilm = (e, id) => {
    onClick();
    let idx = _.findIndex(films, {film_id: filmIdSelected});
    let domRect = e.currentTarget.getBoundingClientRect();
    let line = 0;
    if (screenSize === 'xl') line = Math.floor(idx / 6);
    if (screenSize === 'lg') line = Math.floor(idx / 6);
    if (screenSize === 'md') line = Math.floor(idx / 4);
    if (screenSize === 'sm') line = Math.floor(idx / 3);
    if (screenSize === 'xs') line = Math.floor(idx / 2);
    const container = e.currentTarget.parentNode.parentNode;
    const containerTop = Utils.cumulativeOffset(container).top;
    setPreviewTop(containerTop + (domRect.height + 24) * (line + 0.7));
    setFilmIdSelected(id);
  };

  const handlePreviewResize = () => {
    const domRect = previewRef.current.getBoundingClientRect();
    setPreviewHeight(domRect.height);
  };

  const renderFilmPreview = i => {
    const idx = _.findIndex(films, {film_id: filmIdSelected});

    const length = films.length;

    const nbByLine = FILM_NB_BY_LINE[screenSize];
    const displayPreview = Math.min(idx + nbByLine - (idx % nbByLine), length) - 1 === i;
    const displayClosedPreview = Math.min(i + nbByLine - (i % nbByLine), length) - 1 === i;

    if (canOpen && filmIdSelected > 0 && displayPreview) {
      const filmSelected = _.find(films, {film_id: filmIdSelected});
      return (
        <div className="preview-container preview-container-show" key={i + 'preview'} style={{height: previewHeight}}>
          <div ref={previewRef}>
            <PreviewFilm
              film={filmSelected}
              key={filmIdSelected}
              onSizeChange={handlePreviewResize}
              onClose={() => setFilmIdSelected(null)}
              onExitfullscreen={goToPreview}
            />
          </div>
        </div>
      );
    }

    if (displayClosedPreview) {
      return <div className="preview-container preview-container-hidden" key={i + 'preview'} />;
    }
    return null;
  };

  const nbByLine = FILM_NB_BY_LINE[screenSize];

  const visibleFilms = films.slice(0, nbByLine * visibleLinesNb);

  const areSomeFilmsHidden = visibleLinesNb < Math.ceil(films.length / FILM_NB_BY_LINE[screenSize]);

  return (
    <Grid container spacing={screenSize === 'xs' ? 1 : screenSize === 'sm' ? 2 : 3} ref={containerRef}>
      {visibleFilms.map((film, i) => (
        <React.Fragment key={film.film_id}>
          <Grid item xs={6} sm={4} md={3} lg={2} key={film.film_id}>
            <FilmCartouche film={film} onClick={e => handleSelectFilm(e, film.film_id)} />
          </Grid>
          {renderFilmPreview(i)}
        </React.Fragment>
      ))}
      {areSomeFilmsHidden && visibleLinesNb % SEE_MORE_LINES_INCREMENT === 0 && (
        <div className="seeMoreButton">
          <Button color="primary" variant="outlined" onClick={handleClickSeeMore}>
            {t('voir-plus-de-films')}
          </Button>
        </div>
      )}
    </Grid>
  );
};

export default FilmsList;
